import { ArrowRight } from "lucide-react";
import { getCurrentDomainConfig } from '@core/tenant/getSubdomainPrefix';

interface EventRecommendation {
  text: string;
  url?: string;
}

interface EventRecommendationsProps {
  recommendations: EventRecommendation[];
}

/**
 * Rewrites legacy meitre.com URLs to the current environment.
 * - Local dev: "https://ritual.meitre.com/?x=1" → "http://localhost:8080/?r=ritual&x=1"
 * - Production: rewrites to match the current domain pattern
 * Leaves non-meitre URLs untouched.
 */
function toCurrentDomainUrl(url: string): string {
  if (import.meta.env.DEV) {
    const match = url.match(/^https?:\/\/([^.]+)\.(?:v2\.)?meitre\.com(.*)/);
    if (match) {
      const [, subdomain, rest] = match;
      const parsed = new URL(`http://localhost:8080${rest}`);
      parsed.searchParams.set('r', subdomain);
      return parsed.toString();
    }
    return url;
  }

  const config = getCurrentDomainConfig();
  const baseDomain = config?.baseDomain ?? 'meitre.com';

  return url.replace(
    /^(https?:\/\/[^.]+)\.(v2\.)?meitre\.com/,
    (_match, prefix) => `${prefix}.${baseDomain}`,
  );
}

export default function EventRecommendations({ recommendations }: EventRecommendationsProps) {
  if (recommendations.length === 0) return null;

  return (
    <div>
      <ul className="space-y-3">
        {recommendations.map((item, index) => (
          <li
            key={index}
            className="flex items-center gap-3 rounded-xl bg-black/40 backdrop-blur-[2px] border border-white/10 p-5"
          >
            <p className="flex-1 font-semibold text-white text-[15px] leading-snug drop-shadow">
              {item.text}
            </p>
            {item.url && (
              <a
                href={toCurrentDomainUrl(item.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground transition-opacity hover:opacity-80"
              >
                <ArrowRight size={18} />
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
